import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { autoTranslationQueryKeys, gTranslate } from '@lumapps/auto-translation';
import {
    ADVANCED_END_USER_TRANSLATION_FF,
    AUTO_TRANSLATION_HTML_ALLOWED_ATTRIBUTES,
    AUTO_TRANSLATION_HTML_ALLOWED_TAGS,
    GOOGLE_TRANSLATE_V2_MAX_LENGTH,
    GOOGLE_TRANSLATE_V3_MAX_LENGTH,
} from '@lumapps/auto-translation/constants';
import { useQueries, useQueryClient } from '@lumapps/base-api/react-query';
import { getCurrentContentId } from '@lumapps/contents/ducks/selectors';
import { isFeatureEnabled } from '@lumapps/features';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { getInstanceColorPalette } from '@lumapps/style/ducks/selectors';
import { DEFAULT_LANGUAGE, GLOBAL } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { getConnectedUserLanguage } from '@lumapps/user/ducks/selectors';
import { sanitizeHTML } from '@lumapps/utils/string/sanitizeHtml';
import { WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import { WIDGET_HTML_TYPE } from '@lumapps/widget-html/constants';
import { WREX_VIEW_CLASSNAME } from '@lumapps/wrex/constants';
import { htmlValuesToSlate } from '@lumapps/wrex/serialization/slate/utils/htmlValuesToSlate';

import { queryKeys } from '../api';
import { CLIENT_COMPUTED_WIDGET_TYPE, EDITABLE_WIDGET_LIST } from '../constants';
import { getWidgetEntities } from '../ducks/selectors';
import { actions } from '../ducks/slice';
import { Widget, WidgetTranslatableData } from '../types';
import { parseWidgetsTranslatableDataToTranslatableString } from '../utils/parseWidgetsTranslatableDataToTranslatableString';

interface UseWidgetsAutoTranslateReturn {
    /** Whether the autotranslate call had an error */
    hasError: boolean;
    /** Whether the autotranslate call is loading */
    isLoading: boolean;
    /** The translated widgets string */
    translatedValues?: string[];
    /** Callback triggering the auto translate call */
    onAutoTranslate: () => void;
    /** Callback to set the init translation back  */
    onReset: () => void;
    /** Callback to clear the values like it was a page refresh  */
    onHardReset: () => void;
}

export const useWidgetsAutoTranslate = ({
    targetLanguage,
    sourceLanguage: sourceLang,
    widgetsTranslatableData,
    onTranslationUpdate,
}: {
    targetLanguage?: string;
    sourceLanguage?: string;
    widgetsTranslatableData?: WidgetTranslatableData;
    onTranslationUpdate?: (translatedValues: string[]) => void;
} = {}): UseWidgetsAutoTranslateReturn => {
    const { error } = useNotification();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const widgetEntities = useSelector(getWidgetEntities);
    const currentLanguage = useSelector(currentLanguageSelector);
    const contentId = useSelector(getCurrentContentId);
    const userLanguage = useSelector(getConnectedUserLanguage);
    const colorPalette = useSelector(getInstanceColorPalette);
    const isUsingApiV3 = useSelector(isFeatureEnabled(ADVANCED_END_USER_TRANSLATION_FF));

    const [valuesToTranslate, setValuesToTranslate] = React.useState<string[]>([]);
    const [translationValuesToDisplay, setTranslationValuesToDisplay] = React.useState<string[]>([]);
    const [widgetsNonTranslatableText, setWidgetsNonTranslatableText] = React.useState<Record<string, string>>({});

    const maxCharacters = isUsingApiV3 ? GOOGLE_TRANSLATE_V3_MAX_LENGTH : GOOGLE_TRANSLATE_V2_MAX_LENGTH;
    const targetLang = targetLanguage || userLanguage || currentLanguage || DEFAULT_LANGUAGE;

    const { items, isLoading, hasError } = useQueries({
        queries: valuesToTranslate.map((groupedWidgets) => {
            return {
                enabled: true,
                queryKey: autoTranslationQueryKeys.googleTranslate({
                    content: groupedWidgets,
                    targetLang,
                    sourceLang,
                    isContentHTML: true,
                }),
                queryFn: () =>
                    gTranslate({
                        content: groupedWidgets,
                        targetLang,
                        sourceLang,
                        isContentHTML: true,
                    }),
            };
        }),
    });

    const translatedValues = React.useMemo(() => items?.filter((value) => !!value) as string[], [items]);

    const getWidgetsData = React.useCallback(() => {
        if (widgetsTranslatableData) {
            return widgetsTranslatableData;
        }
        let widgetsToTranslatableData: WidgetTranslatableData = {};
        let foundWidgetsNonTranslatableText = {};

        for (const value of Object.values(widgetEntities)) {
            const widgetHeaderValue = value.header?.label;
            const widgetFooterValue = value.footer?.label;
            let widgetBodyValue = (value.body as any)?.text;

            if (value.widgetType === WIDGET_HTML_TYPE) {
                const htmlValue = (value.body as any)?.html;
                const sanitizedHtmlValue = sanitizeHTML(htmlValue, {
                    allowedTags: AUTO_TRANSLATION_HTML_ALLOWED_TAGS,
                    allowedAttr: AUTO_TRANSLATION_HTML_ALLOWED_ATTRIBUTES,
                });
                widgetBodyValue = sanitizedHtmlValue;

                if (!isEmpty(sanitizedHtmlValue)) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(htmlValue, 'text/html');

                    // Extract all style and scripts tags
                    const nonTranslatableTags = [
                        ...Array.from(doc.querySelectorAll('style, script')).map((tag) => tag.outerHTML),
                    ].join('');

                    if (!isEmpty(nonTranslatableTags)) {
                        foundWidgetsNonTranslatableText = {
                            ...foundWidgetsNonTranslatableText,
                            [value.widgetId]: nonTranslatableTags,
                        };
                    }
                }
            } else if (value.widgetType === WIDGET_CONTRIBUTION_TYPE) {
                widgetBodyValue = document.querySelector(
                    `[data-id=widget-contribution-${value.widgetId}] .${WREX_VIEW_CLASSNAME}`,
                )?.innerHTML;
            }

            // Only keep the widgets that have something to translate
            if (!isEmpty(widgetHeaderValue) || !isEmpty(widgetBodyValue) || !isEmpty(widgetFooterValue)) {
                widgetsToTranslatableData = {
                    ...widgetsToTranslatableData,
                    [value.widgetId]: {
                        title: { valueToTranslate: widgetHeaderValue, shouldAutoTranslate: true },
                        body: { valueToTranslate: widgetBodyValue, shouldAutoTranslate: true },
                        footer: { valueToTranslate: widgetFooterValue, shouldAutoTranslate: true },
                        type:
                            value.widgetType === CLIENT_COMPUTED_WIDGET_TYPE
                                ? value.body?.widgetType || value.widgetType
                                : value.widgetType,
                    },
                };
                dispatch(
                    actions.setWidgetProperties({
                        widgetId: value.widgetId,
                        widgetProperties: { state: 'loading' },
                    }),
                );
            }
        }

        setWidgetsNonTranslatableText(foundWidgetsNonTranslatableText);

        return widgetsToTranslatableData;
    }, [dispatch, widgetEntities, widgetsTranslatableData]);

    /** Concatenate the translatable values until they reach the max characters allowed */
    const getValuesByLimit = React.useCallback(
        (widgetsData: WidgetTranslatableData) => {
            const slicedValues = parseWidgetsTranslatableDataToTranslatableString(widgetsData);
            const valuesByLimit = [];
            let concatenedValue = '';

            for (const currentElement of slicedValues) {
                // Keep adding the value to the concatened string when possible
                if (currentElement.length + concatenedValue.length <= maxCharacters) {
                    concatenedValue += currentElement;
                }
                // When not possible anymore, this means we have a max translatable item
                // We then need to add it to the array
                else {
                    if (concatenedValue) {
                        valuesByLimit.push(concatenedValue);
                    }
                    concatenedValue = currentElement;
                }
            }

            // Used to handle last iteration that was added to concatenedValue but not pushed
            if (concatenedValue) {
                valuesByLimit.push(concatenedValue);
            }

            return valuesByLimit;
        },
        [maxCharacters],
    );

    const onAutoTranslate = React.useCallback(async () => {
        // Re-use the previously translated values when auto-translating again after a reset
        if (translatedValues && translatedValues.length > 0) {
            setTranslationValuesToDisplay(translatedValues);
        }
        // Get the values to translate to trigger the translation call
        else {
            const widgetsData = getWidgetsData();
            const valuesByLimit = getValuesByLimit(widgetsData);
            setValuesToTranslate(valuesByLimit);
        }
    }, [getValuesByLimit, getWidgetsData, translatedValues]);

    /** Reset to init translations */
    const onReset = React.useCallback(() => {
        setTranslationValuesToDisplay(valuesToTranslate);
    }, [valuesToTranslate]);

    /** Clear the values like it was a page refresh */
    const onHardReset = React.useCallback(() => {
        setTranslationValuesToDisplay([]);
        setValuesToTranslate([]);
    }, []);

    /** Update the widget properties by using the translation */
    const updateWidgetsWithTranslations = React.useCallback(
        (widgetsHtmlTranslation: (string | undefined)[]) => {
            if (widgetsHtmlTranslation.length > 0) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(widgetsHtmlTranslation.join(''), 'text/html');
                const widgets = doc.querySelectorAll('article');

                widgets.forEach((widget) => {
                    const id = widget.dataset.widgetId;
                    const type = widget.dataset.widgetType || '';
                    if (!id) {
                        return;
                    }

                    const header = widget.querySelector('header');
                    const main = widget.querySelector('main');
                    const footer = widget.querySelector('footer');

                    let widgetTranslatedData: {
                        header?: Widget['header'];
                        footer?: Widget['footer'];
                        body?: any;
                    } = {};

                    if (header && header.textContent) {
                        widgetTranslatedData = { ...widgetTranslatedData, header: { label: header.textContent } };
                    }
                    if (main && main.textContent) {
                        let bodyValue;

                        if (type === WIDGET_CONTRIBUTION_TYPE) {
                            const htmlToSlate = htmlValuesToSlate({
                                slateHtml: main.innerHTML,
                                pasteOptions: { colorPalette },
                            });
                            bodyValue = { richText: { children: htmlToSlate } };
                        } else if (type === WIDGET_HTML_TYPE) {
                            const htmlNonTranslatableText = widgetsNonTranslatableText[id];
                            bodyValue = htmlNonTranslatableText
                                ? { html: `${main.innerHTML}${htmlNonTranslatableText}` }
                                : { html: main.innerHTML };
                        } else {
                            bodyValue = { text: main.textContent };
                        }
                        widgetTranslatedData = { ...widgetTranslatedData, body: bodyValue };
                    }
                    if (footer && footer.textContent) {
                        widgetTranslatedData = { ...widgetTranslatedData, footer: { label: footer.textContent } };
                    }

                    const widgetQueryKey = queryKeys.getWidgetBlocks({
                        ownerResourceId: contentId,
                        ownerResourceType: 'content',
                        widgetType: type,
                        widgetId: id,
                        language: currentLanguage,
                    });

                    // We still rely on redux for the editable widgets are they doesn't use react query
                    if (EDITABLE_WIDGET_LIST.includes(type)) {
                        dispatch(
                            actions.setWidgetProperties({
                                widgetId: id,
                                widgetProperties: { state: 'loaded', ...widgetTranslatedData },
                            }),
                        );
                    } else {
                        queryClient.setQueryData(widgetQueryKey, (previousData?: { pages: any[] }) => {
                            const previousWidgetData = previousData?.pages?.[0];
                            const newWidgetData = {
                                ...(widgetTranslatedData?.header && {
                                    header: { ...previousWidgetData?.header, ...widgetTranslatedData.header },
                                }),
                                ...(widgetTranslatedData?.body && {
                                    body: { ...previousWidgetData?.body, ...widgetTranslatedData.body },
                                }),
                                ...(widgetTranslatedData?.footer && {
                                    footer: { ...previousWidgetData?.footer, ...widgetTranslatedData.footer },
                                }),
                            };

                            return {
                                ...previousData,
                                pages: [
                                    {
                                        ...previousWidgetData,
                                        state: 'loaded',
                                        ...newWidgetData,
                                    },
                                ],
                            };
                        });
                    }
                });
            }
        },
        [colorPalette, contentId, currentLanguage, dispatch, queryClient, widgetsNonTranslatableText],
    );

    React.useEffect(() => {
        if (hasError) {
            error({ translate: GLOBAL.GENERIC_ERROR });
        }
    }, [hasError, error]);

    React.useEffect(() => {
        if (!!translatedValues && translatedValues.length > 0 && isEmpty(translationValuesToDisplay)) {
            setTranslationValuesToDisplay(translatedValues);
        }
    }, [translatedValues, translationValuesToDisplay]);

    React.useEffect(() => {
        if (!isEmpty(translationValuesToDisplay)) {
            if (onTranslationUpdate) {
                onTranslationUpdate(translationValuesToDisplay);
            } else {
                updateWidgetsWithTranslations(translationValuesToDisplay);
            }
        }
    }, [translationValuesToDisplay, updateWidgetsWithTranslations, onTranslationUpdate]);

    return {
        translatedValues,
        hasError,
        isLoading,
        onAutoTranslate,
        onReset,
        onHardReset,
    };
};
